import { useState, useRef, useEffect } from 'react';

/**
 * Saved filter views for a list page, kept in this browser only
 * (localStorage, per page). A view is just a name plus the page's filter
 * state; applying it hands that state back to the page. Storage can be
 * unavailable (private windows, blocked site data), so every access is
 * guarded and the feature simply shows nothing saved in that case.
 */
function readViews(storageKey) {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) { return []; }
}

function writeViews(storageKey, views) {
  try { window.localStorage.setItem(storageKey, JSON.stringify(views)); return true; } catch (e) { return false; }
}

export default function SavedViews({ pageKey, currentState, canSave, onApply }) {
  const storageKey = `param_saved_views_${pageKey}`;
  const [views, setViews] = useState(() => readViews(storageKey));
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = e => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setNaming(false); } };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function save() {
    const trimmed = name.trim();
    if (!trimmed) return;
    const next = [...views.filter(v => v.name !== trimmed), { name: trimmed, state: currentState }];
    if (writeViews(storageKey, next)) setViews(next);
    setName('');
    setNaming(false);
  }

  function remove(viewName) {
    const next = views.filter(v => v.name !== viewName);
    if (writeViews(storageKey, next)) setViews(next);
  }

  return (
    <div className="multi-filter" ref={ref}>
      <button type="button" className="multi-filter-button" onClick={() => setOpen(o => !o)}>
        ☆ Views{views.length > 0 && <span className="multi-filter-count">{views.length}</span>}
      </button>
      {open && (
        <div className="multi-filter-menu" style={{ right: 0, left: 'auto' }}>
          {views.length === 0 && !naming && <div className="multi-filter-empty">No saved views yet. Set some filters, then save them here.</div>}
          {views.map(v => (
            <div key={v.name} className="saved-view-row">
              <button type="button" className="saved-view-apply" onClick={() => { onApply(v.state); setOpen(false); }}>{v.name}</button>
              <button type="button" className="saved-view-delete" onClick={() => remove(v.name)} aria-label={`Delete view ${v.name}`}>×</button>
            </div>
          ))}
          {naming ? (
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input className="multi-filter-search" style={{ marginBottom: 0 }} autoFocus placeholder="View name" value={name}
                onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') save(); }} />
              <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={!name.trim()}>Save</button>
            </div>
          ) : (
            <button type="button" className="btn btn-ghost btn-sm" style={{ width: '100%', marginTop: 4 }} disabled={!canSave} onClick={() => setNaming(true)}
              title={canSave ? '' : 'Set at least one filter first'}>
              + Save current filters
            </button>
          )}
          <div className="multi-filter-empty" style={{ paddingTop: 6 }}>Saved in this browser only.</div>
        </div>
      )}
    </div>
  );
}
